import { get } from 'lodash-es'
import FulfillmentRestrictions from '../maps/fulfillmentRestrictions'
import ItemUtils from './item-utils'

/**
 * Utils for fulfillments
 */
class FulfillmentUtils {

  /**
   * Determines if a fulfillment restriction is an out of stock restriction
   * @param {number} rCode The restriction code for a fulfillment
   */
  static isOutOfStock(rCode) {
    return rCode === FulfillmentRestrictions.OUT_OF_STOCK_ONLINE
      || rCode === FulfillmentRestrictions.OUT_OF_STOCK_STERLING
  }

  static isPartiallyOutOfStockSterling(rCode) {
    return rCode === FulfillmentRestrictions.PARTIAL_OUT_OF_STOCK_STERLING
  }

  static isPartiallyOutOfStockOnline(rCode) {
    return rCode === FulfillmentRestrictions.PARTIAL_OUT_OF_STOCK_ONLINE
  }

  static isPartiallyOutOfStock(rCode) {
    return this.isPartiallyOutOfStockSterling(rCode) || this.isPartiallyOutOfStockOnline(rCode)
  }

  static hasSifRestriction(rCode) {
    return rCode === FulfillmentRestrictions.SIF_RESTRICTION
  }

  static getAvailableInventory(options = {}) {
    let qty = 0

    if (options.hasSifRestriction) {
      if (options.storeInventory > options.totalQuantityInDCs) {
        return options.storeInventory
      }

      return options.totalQuantityInDCs
    } else if (options.isPartiallyOutOfStockOnline) {
      if (options.fulfillmentType === 'SHIP' && !options.isStoreFulfillable) {
        return options.totalQuantityInDCs
      }

      return options.sellableQuantity
    } else if (options.fulfillments) {
      options.fulfillments.forEach((fulfillment) => {
        qty += fulfillment.fulfillmentQty
      })
    }

    return qty
  }

  static isUnavailable(restrictions = []) {
    let hasUnavailableRestriction = false

    restrictions.forEach((restriction) => {
      if (FulfillmentUtils.isOutOfStock(restriction.code)) hasUnavailableRestriction = true
    })

    return hasUnavailableRestriction
  }

  static allItemsHaveFulfillmentInfo({ lineItems, fulfillmentsModel, isOrderSummary }) {
    let allHaveFulfillment = true

    lineItems.forEach((item) => {
      const fulfillment = isOrderSummary ? get(item, 'fulfillment')
        : get(fulfillmentsModel, `[${item.reiRefId}].fulfillments`)

      if (!fulfillment) allHaveFulfillment = false
    })

    return allHaveFulfillment
  }

  static getUnavailableProducts(products, fulfillmentsModel, useAudit) {
    const unavailableProducts = []

    products.forEach((item) => {
      // On review and submit we want to use the fulfillment data from audit rather than
      // Fulfillment Service. The structure is slightly different
      const restrictions = useAudit
        ? (get(item, 'fulfillment.restrictions', []) || [])
        : get(fulfillmentsModel, `[${item.reiRefId}].fulfillments['0'].shipMethodsInformation.restrictions`, [])

      if (restrictions.length) {
        const fulfillmentInfos = useAudit
          ? get(item, 'fulfillment.fulfillmentInfos')
          : get(fulfillmentsModel, `[${item.reiRefId}].fulfillments['0'].shipMethodsInformation.allowedShipMethods['0'].fulfillmentInfos`, [])
        const isStoreFulfillable = useAudit
          ? get(item, 'rspuable', false)
          : get(fulfillmentsModel, `[${item.reiRefId}].skuInformation.storeFulfillable`, false)
        const fulfillmentType = useAudit
          ? get(item, 'shipMethod.fulfillmentType', 'SHIP')
          : get(fulfillmentsModel, `[${item.reiRefId}].fulfillments['0'].fulfillmentType`, 'SHIP')
        let isOutOfStock = false
        let hasSifRestriction = false
        let isPartiallyOutOfStockOnline = false
        let isPartiallyOutOfStockSterling = false

        restrictions.forEach((restriction) => {
          const code = restriction.code

          if (FulfillmentUtils.isOutOfStock(code)) {
            isOutOfStock = true
          } else if (FulfillmentUtils.hasSifRestriction(code)) {
            hasSifRestriction = true
          } else if (FulfillmentUtils.isPartiallyOutOfStockOnline(code)) {
            isPartiallyOutOfStockOnline = true
          } else if (FulfillmentUtils.isPartiallyOutOfStockSterling(code)) {
            isPartiallyOutOfStockSterling = true
          }
        })

        if (isOutOfStock || isPartiallyOutOfStockOnline
          || hasSifRestriction || isPartiallyOutOfStockSterling) {
          unavailableProducts.push(Object.assign({}, item, {
            availability: {
              quantity: isOutOfStock
                ? 0
                : FulfillmentUtils.getAvailableInventory({
                  fulfillmentType,
                  hasSifRestriction,
                  isStoreFulfillable,
                  isPartiallyOutOfStockOnline,
                  fulfillments: fulfillmentInfos,
                  sellableQuantity: useAudit
                    ? get(item, 'fulfillment.sellableQuantity', 0)
                    : get(fulfillmentsModel, `[${item.reiRefId}].skuInformation.sellableQuantity`, 0),
                  totalQuantityInDCs: useAudit
                    ? get(item, 'fulfillment.totalQuantityInDCs', 0)
                    : get(fulfillmentsModel, `[${item.reiRefId}].skuInformation.totalQuantityInDCs`, 0),
                  storeInventory: get(fulfillmentsModel, `[${item.reiRefId}].fulfillments['0'].pickupStoreRawSellableInventory.quantity`, 0),
                }),
            },
          }))
        }
      }
    })

    return unavailableProducts
  }

  static getBackorderedProducts(products) {
    const backorderedProducts = []

    products.forEach((item) => {
      if (ItemUtils.isBackorderItem(item)) {
        backorderedProducts.push(Object.assign({}, item, {
          availability: {
            quantity: ItemUtils.isCompletelyBackordered(item)
              ? 0
              : item.quantity - ItemUtils.getBackorderedInventory(item),
          },
        }))
      }
    })

    return backorderedProducts
  }
}

export default FulfillmentUtils
